import { useEffect, useState } from "react";
import { Link } from "wouter";
import {
  Button,
  Card,
  Container,
  Group,
  Stack,
  Text,
  Title,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Table,
} from "@mantine/core";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconArrowsExchange,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { getMappingProfiles, deleteMappingProfile } from "../api";
import { getPimSchemas } from "../Pim/pimApi";
import { useStore } from "../store";
import Timeago from "../../../Boilerplate/_Shared/Timeago";

export default function MappingProfiles() {
  const { t } = useTranslation("imports");
  const profile = useStore((state) => state.profile);
  const [profiles, setProfiles] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper to get schema name by ID
  const getSchemaName = (schemaId) => {
    if (!schemaId) return "—";
    const schema = schemas.find((s) => s.id === schemaId);
    return schema?.labels?.en || schema?.name || schemaId;
  };

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await getMappingProfiles();
      setProfiles(data);
    } catch (error) {
      notifications.show({
        color: "red",
        title: t("mappings.profiles.loadFailedTitle"),
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await loadProfiles();
      // Load schemas for name lookup
      if (profile?.tenantId) {
        try {
          const schemaList = await getPimSchemas(profile.tenantId);
          setSchemas(schemaList || []);
        } catch {
          // Silently fail - schema names will fallback to IDs
        }
      }
    };
    loadData();
  }, [profile?.tenantId]);

  const handleDelete = (profile) => {
    modals.openConfirmModal({
      title: t("mappings.deleteProfile.title"),
      children: (
        <Stack gap="sm">
          <Text>
            {t("mappings.deleteProfile.bodyBefore")}
            <strong>{profile.name}</strong>
            {t("mappings.deleteProfile.bodyAfter")}
          </Text>
          <Text c="red" size="sm" fw={600}>
            {t("mappings.deleteProfile.warning")}
          </Text>
        </Stack>
      ),
      labels: {
        confirm: t("actions.delete"),
        cancel: t("common:generic.action_cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deleteMappingProfile(profile.id);
          notifications.show({
            color: "green",
            title: t("mappings.common.successTitle"),
            message: t("mappings.deleteProfile.success"),
          });
          loadProfiles();
        } catch (error) {
          notifications.show({
            color: "red",
            title: t("mappings.deleteProfile.errorTitle"),
            message: error.message,
          });
        }
      },
    });
  };

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <div>
          <Title order={2}>{t("mappings.profiles.title")}</Title>
          <Text c="dimmed" size="sm">
            {t("mappings.profiles.subtitle")}
          </Text>
        </div>
        <Button
          component={Link}
          href="/mapping-profiles/new"
          leftSection={<IconPlus size={16} />}
        >
          {t("mappings.profiles.new")}
        </Button>
      </Group>

      {profiles.length === 0 ? (
        <Card withBorder>
          <Stack align="center" gap="md" py="xl">
            <IconArrowsExchange size={48} stroke={1.5} color="gray" />
            <div style={{ textAlign: "center" }}>
              <Text fw={500} size="lg">
                {t("mappings.profiles.empty")}
              </Text>
              <Text c="dimmed" size="sm">
                {t("mappings.profiles.emptyHint")}
              </Text>
            </div>
            <Button
              component={Link}
              href="/mapping-profiles/new"
              leftSection={<IconPlus size={16} />}
            >
              {t("mappings.profiles.new")}
            </Button>
          </Stack>
        </Card>
      ) : (
        <Card withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("mappings.profiles.name")}</Table.Th>
                <Table.Th>{t("mappings.profile.sourceSchema")}</Table.Th>
                <Table.Th>{t("mappings.targetSchema")}</Table.Th>
                <Table.Th ta="center">{t("mappings.profiles.rules")}</Table.Th>
                <Table.Th>{t("mappings.created")}</Table.Th>
                <Table.Th ta="right">{t("mappings.profiles.actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {profiles.map((profile) => (
                <Table.Tr
                  key={profile.id}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    (window.location.href = `/mapping-profiles/${profile.id}`)
                  }
                >
                  <Table.Td>
                    <Text fw={500} c="blue">
                      {profile.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {getSchemaName(profile.sourceSchemaId)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {getSchemaName(profile.targetSchemaId)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Text size="sm">{profile.ruleCount}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      <Timeago date={profile.createdAt} />
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      <Tooltip label={t("mappings.profiles.editTooltip")}>
                        <ActionIcon
                          component={Link}
                          href={`/mapping-profiles/${profile.id}`}
                          variant="subtle"
                          color="blue"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("mappings.profiles.deleteTooltip")}>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(profile);
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}
    </Container>
  );
}
